import { useMutation } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { useState } from 'react';
import styled from 'styled-components';

import { color, dimension } from '@creditornot/cb-ingredients/design-tokens';
import {
  PromotionRecommendationsApproveRequest,
  PromotionRecommendationsBulkActionFilter,
  PromotionRecommendationsBulkApproveRequest,
  PromotionRecommendationsBulkRejectRequest,
  PromotionRecommendationsRejectRequest,
} from 'common/api-types/recommendations';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { SelectionState } from 'common/components/cookbook/DataTable/types';
import { SortButton } from 'common/components/cookbook/SortButton';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useSetSearch } from 'common/hooks/useSetSearch';
import { useApiQueryClient } from 'common/query/useApiQueryClient';
import { AxiosPromotionRecommendationsError, LocalizedMessageId } from 'common/types';
import { downloadBlob } from 'common/utils/downloadUtils';
import {
  selectionRecommendationsRouteApi,
  SelectionRecommendationsSearchRouteSchemaType,
} from 'features/selection-recommendations';
import {
  approvePromotionRecommendations,
  bulkApprovePromotionRecommendations,
  bulkRejectPromotionRecommendations,
  rejectPromotionRecommendations,
} from 'features/selection-recommendations/api';
import { FilterSelect } from 'features/selection-recommendations/common/FilterSelect/FilterSelect';
import { ActionSelect } from 'features/selection-recommendations/common/RecommendationActions/ActionSelect';
import { ApproveRecommendationModal } from 'features/selection-recommendations/common/RecommendationActions/DownloadApproved/ApproveRecommendationModal';
import {
  PROMOTION_RECOMMENDATIONS_QUERY_KEY_ROOT,
  usePromotionRecommendationsInfiniteQuery,
} from 'features/selection-recommendations/query';
import { RecommendationAction } from 'features/selection-recommendations/types';

import { selectedProductFamilyIdsAtom, selectedRecommendationsAtom } from '../state';
import { PromotionRecommendationsList } from './PromotionRecommendationsList';
import { RejectPromotionsForm } from './RejectRecommendations/RejectingPromotionsForm';
import { sortParamMap } from './sortParamMap';
import {
  PromotionRecommendationRejectionFormValues,
  PromotionRecommendationsSortType,
} from './types';
import { getRecommendationItems } from './utils';

const Header = styled(ContentContainer)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${dimension.spaceRegularXlarge};
  overflow: auto;
`;

const HorizontalDivider = styled.div`
  height: 20px;
  border-left: 2px solid ${color.border};
`;

export const PromotionRecommendation = () => {
  const [countryCode] = useSelectedCountryCodes();
  const [selectedState, setSelectedState] = useAtom(selectedRecommendationsAtom);
  const selectedProductFamilyIds = useAtomValue(selectedProductFamilyIdsAtom);
  const { bulkSelect, unselected, selected } = selectedState;
  const [action, setAction] = useState<RecommendationAction | null>(null);
  const queryClient = useApiQueryClient();
  const f = useFormatMessage();

  const {
    promotionsSortBy = 'EXPECTED_BUSINESS_IMPACT_DESC',
    venueIds = [],
    brandIds = [],
    vendorIds = [],
  } = selectionRecommendationsRouteApi.useSearch();
  const setSearch = useSetSearch<SelectionRecommendationsSearchRouteSchemaType>();
  const sortParams = sortParamMap[promotionsSortBy];
  const sortingDirection = sortParams.sortingDirection;

  const {
    data,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetching,
    totalCount,
    promotionRecommendations,
  } = usePromotionRecommendationsInfiniteQuery(countryCode, {
    promotionRecommendationsSortBy: sortParams.sortBy,
    sortingDirection,
    pageSize: 100,
    filter: {
      productFamilyIds: selectedProductFamilyIds,
      venueIds,
      brandIds,
      vendorIds,
    },
  });

  const resetSelectionState = () => {
    setSelectedState({ bulkSelect: false, selected: [], unselected: [] });
    setAction(null);
  };

  const invalidateQuery = async () => {
    resetSelectionState();

    await queryClient.invalidateQueries({
      queryKey: [PROMOTION_RECOMMENDATIONS_QUERY_KEY_ROOT],
    });
  };

  const onDownloadSuccess = async (blob: Blob) => {
    const fileName = 'promotion_recommendations.csv';
    downloadBlob(new Blob([blob]), fileName);
    await invalidateQuery();
  };

  const {
    mutate: approveMutate,
    isPending: approveIsPending,
    error: approveError,
  } = useMutation<Blob, AxiosPromotionRecommendationsError, PromotionRecommendationsApproveRequest>(
    {
      mutationFn: approvePromotionRecommendations,
      onSuccess: onDownloadSuccess,
    },
  );

  const {
    mutate: rejectMutate,
    isPending: rejectIsPending,
    error: rejectError,
  } = useMutation<Blob, AxiosPromotionRecommendationsError, PromotionRecommendationsRejectRequest>({
    mutationFn: rejectPromotionRecommendations,
    onSuccess: invalidateQuery,
  });

  const {
    mutate: bulkApproveMutate,
    isPending: bulkApproveIsPending,
    error: bulkApproveError,
  } = useMutation<
    Blob,
    AxiosPromotionRecommendationsError,
    PromotionRecommendationsBulkApproveRequest
  >({
    mutationFn: bulkApprovePromotionRecommendations,
    onSuccess: onDownloadSuccess,
  });

  const {
    mutate: bulkRejectMutate,
    isPending: bulkRejectIsPending,
    error: bulkRejectError,
  } = useMutation<
    Blob,
    AxiosPromotionRecommendationsError,
    PromotionRecommendationsBulkRejectRequest
  >({
    mutationFn: bulkRejectPromotionRecommendations,
    onSuccess: invalidateQuery,
  });

  const onRowSelectChange = (value: 'ALL' | SelectionState) => {
    setSelectedState(value === 'ALL' ? { bulkSelect: true, selected: [], unselected: [] } : value);
  };

  const requestedAt = data?.pages[0]?.meta.requestedAt;
  const selectedIds = selected.flatMap((current) => current.selectedSubrows ?? []);
  const selectedRecommendationCount = bulkSelect
    ? totalCount - unselected.length
    : selectedIds.length;

  const handleDownload = () => {
    if (!requestedAt) {
      return;
    }

    if (!bulkSelect) {
      approveMutate({
        recommendations: getRecommendationItems(selectedIds, promotionRecommendations),
        countryCode,
      });

      return;
    }

    const unselectedRecommendationItems = getRecommendationItems(
      unselected,
      promotionRecommendations,
    );

    const bulkActionFilter: PromotionRecommendationsBulkActionFilter = {
      countryCode,
      excludedRecommendationItems: unselectedRecommendationItems,
      productFamilyIds: selectedProductFamilyIds,
      vendorIds,
      venueIds,
      brandIds,
    };

    bulkApproveMutate({
      filter: bulkActionFilter,
      meta: {
        expectedActionsCount: selectedRecommendationCount,
        recommendationsRequestedAt: requestedAt,
      },
    });
  };

  const handleReject = (values: PromotionRecommendationRejectionFormValues) => {
    if (!requestedAt) {
      return;
    }

    if (!bulkSelect) {
      const selectedRecommendationItems = getRecommendationItems(
        selectedIds,
        promotionRecommendations,
      );

      rejectMutate({
        recommendations: selectedRecommendationItems,
        countryCode,
        reason: values.reason,
        comment: values.comment,
      });

      return;
    }

    const unselectedRecommendationItems = getRecommendationItems(
      unselected,
      promotionRecommendations,
    );

    const bulkActionFilter: PromotionRecommendationsBulkActionFilter = {
      countryCode,
      excludedRecommendationItems: unselectedRecommendationItems,
      productFamilyIds: selectedProductFamilyIds,
      vendorIds,
      venueIds,
      brandIds,
    };

    bulkRejectMutate({
      filter: bulkActionFilter,
      meta: {
        expectedActionsCount: selectedRecommendationCount,
        recommendationsRequestedAt: requestedAt,
      },
      reason: values.reason,
      comment: values.comment,
    });
  };

  const approvePromotionRecommendationError = approveError || bulkApproveError;
  const approvalIsPending = approveIsPending || bulkApproveIsPending;
  const rejectPromotionRecommendationError = rejectError || bulkRejectError;
  const rejectPromotionRecommendationIsPending = rejectIsPending || bulkRejectIsPending;

  const options = Object.entries(sortParamMap).map(([key]) => ({
    label: f(`selection_recommendations.sort_by:${key}` as LocalizedMessageId),
    sortBy: key as PromotionRecommendationsSortType,
    sortingType: sortingDirection,
  }));

  const handleSortBy = async (sortBy: PromotionRecommendationsSortType) => {
    await resetSelectionState();
    setSearch({ promotionsSortBy: sortBy });
  };

  return (
    <>
      <Header borderBottom={true} verticalPadding="sm">
        <Stack direction="row" gap="regularXlarge" alignItems="center">
          <SortButton
            sortBy={promotionsSortBy}
            options={options}
            onSortChange={handleSortBy}
            sortingType={sortingDirection}
          />
          <HorizontalDivider />
        </Stack>
        <FilterSelect
          resetSelectionState={resetSelectionState}
          filters={['venue', 'brand', 'vendor']}
        />
      </Header>
      <PromotionRecommendationsList
        dataSource={promotionRecommendations}
        isFetching={isFetching}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isLoading={isLoading}
        totalCount={totalCount}
        rowSelection={{
          onRowSelectChange,
          selectedRows: bulkSelect && !unselected.length ? 'ALL' : selectedIds,
        }}
        error={error}
      />
      <ActionSelect
        isVisible={!!selectedRecommendationCount}
        handleRejectionModalOpen={() => {
          setAction('REJECT_RECOMMENDATIONS');
        }}
        handleApprovalModalOpen={() => {
          setAction('APPROVE_RECOMMENDATIONS');
        }}
      />
      <ApproveRecommendationModal
        isOpen={action === 'APPROVE_RECOMMENDATIONS'}
        onDismiss={() => setAction(null)}
        isDownloading={approvalIsPending}
        onDownload={handleDownload}
        error={approvePromotionRecommendationError}
        description={f('selection_recommendations.approve_promotion_recommendation_description', {
          count: selectedRecommendationCount,
        })}
      />
      <RejectPromotionsForm
        isOpen={action === 'REJECT_RECOMMENDATIONS'}
        onDismiss={() => setAction(null)}
        onReject={handleReject}
        rejectIsPending={rejectPromotionRecommendationIsPending}
        selectedRecommendationCount={selectedRecommendationCount}
        error={rejectPromotionRecommendationError}
        canDownload={false}
      />
    </>
  );
};
